import { X } from "lucide-react";
import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import "./modal-sheet.css";

interface ModalProps {
  id: string;
  title: string;
  children: ReactNode;
  onDismiss: () => void;
  initialFocusSelector?: string;
  placement?: "center" | "bottom";
}

/** Shared modal shell; native top-layer presentation keeps background controls inert and keyboard focus inside. */
export function Modal({
  id,
  title,
  children,
  onDismiss,
  initialFocusSelector,
  placement = "center",
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const previousFocus =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = "hidden";
    // Keep bottom sheets on the same mobile surface as their trigger, including centered desktop frames.
    const frame =
      placement === "bottom"
        ? (previousFocus?.closest<HTMLElement>(".rounded-shell") ??
          document.querySelector<HTMLElement>(".rounded-shell"))
        : null;
    const fitSheet = () => {
      if (!frame?.isConnected) return;
      const bounds = frame.getBoundingClientRect();
      dialog.style.left = `${bounds.left + 2}px`;
      dialog.style.right = "auto";
      dialog.style.bottom = `${Math.max(0, window.innerHeight - bounds.bottom + 2)}px`;
      dialog.style.width = `${Math.max(0, bounds.width - 4)}px`;
      dialog.style.maxHeight = `${Math.max(0, bounds.height * 0.9)}px`;
      dialog.style.borderBottomLeftRadius = "28px";
      dialog.style.borderBottomRightRadius = "28px";
    };
    const resize = frame ? new ResizeObserver(fitSheet) : null;
    if (frame) {
      fitSheet();
      resize?.observe(frame);
      window.addEventListener("resize", fitSheet);
    }
    if (initialFocusSelector)
      dialog.querySelector<HTMLElement>(initialFocusSelector)?.focus();
    return () => {
      dialog.close();
      resize?.disconnect();
      window.removeEventListener("resize", fitSheet);
      document.body.style.overflow = previousOverflow;
      // Restore focus only while the invoking page is still mounted, including during Strict Mode cleanup.
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [initialFocusSelector, placement]);

  // Portaling also separates modal controls from a surrounding form's submit and disabled-fieldset behavior.
  if (typeof document === "undefined") return null;
  return createPortal(
    <dialog
      ref={dialogRef}
      id={id}
      aria-labelledby={titleId}
      aria-modal="true"
      dir="rtl"
      data-placement={placement}
      className={
        placement === "bottom"
          ? "kayer-modal-sheet border-0 bg-white text-slate-700 shadow-xl backdrop:bg-slate-900/40"
          : "fixed inset-0 m-auto max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-sm overflow-y-auto overscroll-contain rounded-2xl border-0 bg-white p-4 text-slate-700 shadow-xl backdrop:bg-slate-900/40"
      }
      onCancel={(event) => {
        event.preventDefault();
        onDismiss();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        // Backdrop clicks target the dialog itself; clicks on its interior padding must keep it open.
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        )
          onDismiss();
      }}
    >
      {placement === "bottom" && (
        <div
          className="mx-auto mt-3 h-1 w-10 shrink-0 rounded-full bg-slate-200"
          aria-hidden="true"
        />
      )}
      <div
        className={
          placement === "bottom"
            ? "flex shrink-0 items-center justify-between gap-3 border-b border-slate-100 px-5 py-4"
            : "mb-4 flex items-center justify-between gap-3"
        }
      >
        <h2 id={titleId} className="text-sm font-bold">
          {title}
        </h2>
        <button
          type="button"
          aria-label="بستن پنجره"
          className="focus-visible:outline-brand flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 focus-visible:outline-2"
          onClick={onDismiss}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {children}
    </dialog>,
    document.body,
  );
}
